// MenuContent.tsx
import { Fragment, useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Collapse,
} from '@mui/material';
import {
  HomeRounded as HomeRoundedIcon,
  AssignmentRounded as AssignmentRoundedIcon,
  VideoLibraryRounded as VideoLibraryRoundedIcon,
  DrawRounded as DrawIcon,
  MeetingRoomRounded as MeetingRoomRoundedIcon,
  WalletRounded as WalletRoundedIcon,
  SchoolRounded,
  GroupRounded,
  ExpandLess,
  ExpandMore,
  Bookmark
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

const videoItems = [
  { text: 'Staff', icon: <GroupRounded />, url: '/videos/staff' },
  { text: 'Students', icon: <SchoolRounded />, url: '/videos/students' },
  { text: 'Saved', icon: <Bookmark />, url: '/videos/saved' },
];

const documentItems = [
  { text: 'Staff', icon: <GroupRounded />, url: '/guides/staff' },
  { text: 'Students', icon: <SchoolRounded />, url: '/guides/students' },
  { text: 'Saved', icon: <Bookmark />, url: '/guides/saved' },
];

const mainListItems = [
  { text: 'Home', icon: <HomeRoundedIcon />, url: '/' },
  { text: 'Videos', icon: <VideoLibraryRoundedIcon />, subMenu: true, subMenuItems: videoItems },
  { text: 'Guides', icon: <AssignmentRoundedIcon />, subMenu: true, subMenuItems: documentItems },
  { text: 'Journal', icon: <DrawIcon />, url: '/document/1' },
  { text: 'Professional Digital Portfolio', icon: <WalletRoundedIcon />, url: '/additional-development' },
];

interface MenuContentProps {
  toggleDrawer?: (newOpen: boolean) => () => void;
  isMobile?: boolean;
  isVideosOpenMobile?: boolean;
  setIsVideosOpenMobile?: (value: boolean) => void;
  isDocumentsOpenMobile?: boolean;
  setIsDocumentsOpenMobile?: (value: boolean) => void;
}

export default function MenuContent({
  toggleDrawer,
  isMobile = false,
  isVideosOpenMobile,
  setIsVideosOpenMobile,
  isDocumentsOpenMobile,
  setIsDocumentsOpenMobile,
}: MenuContentProps) {
  const { instance } = useMsal();
  const location = useLocation();

  const [isHoveringVideos, setIsHoveringVideos] = useState(false);
  const [isHoveringGuides, setIsHoveringGuides] = useState(false);

  const isOnVideosRoute = location.pathname.startsWith('/videos');
  const isOnGuidesRoute = location.pathname.startsWith('/guides');

  const isVideosOpen = isMobile
    ? isVideosOpenMobile
    : isHoveringVideos || isOnVideosRoute;

  const isGuidesOpen = isMobile
    ? isDocumentsOpenMobile
    : isHoveringGuides || isOnGuidesRoute;

  useEffect(() => {
    if (!isMobile) return;

    if (!isOnVideosRoute && setIsVideosOpenMobile) {
      setIsVideosOpenMobile(false);
    }

    if (!isOnGuidesRoute && setIsDocumentsOpenMobile) {
      setIsDocumentsOpenMobile(false);
    }

  }, [location.pathname, 
      isMobile, 
      isOnVideosRoute, 
      setIsVideosOpenMobile, 
      isOnGuidesRoute, 
      setIsDocumentsOpenMobile
    ]);


  const handleLogout = () => instance.logoutRedirect().catch(console.error);
  const closeDrawer = () => toggleDrawer?.(false)();

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>
        {mainListItems.map((item) => {
          const isGuides = item.text === 'Guides';
          const isVideos = item.text === 'Videos';

          const open =
            isVideos ? isVideosOpen :
            isGuides ? isGuidesOpen :
            false;

          return (
            <Fragment key={item.text}>
              <ListItem
                disablePadding
                onMouseEnter={
                  !isMobile && item.subMenu
                    ? () => {
                        if (isVideos) setIsHoveringVideos(true);
                        if (isGuides) setIsHoveringGuides(true);
                      }
                    : undefined
                }
                onMouseLeave={
                  !isMobile && item.subMenu
                    ? () => {
                        if (isVideos) setIsHoveringVideos(false);
                        if (isGuides) setIsHoveringGuides(false);
                      }
                    : undefined
                }
              >
                <ListItemButton
                  component={item.url && !item.subMenu ? Link : 'div'}
                  to={item.url ?? undefined}
                  onClick={() => {
                    if (item.subMenu && isMobile) {
                      if (isVideos && setIsVideosOpenMobile) {
                        setIsVideosOpenMobile(!isVideosOpenMobile);
                      }

                      if (isGuides && setIsDocumentsOpenMobile) {
                        setIsDocumentsOpenMobile(!isDocumentsOpenMobile);
                      }
                    } else if (!item.subMenu) {
                      closeDrawer();
                    }
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />

                  {item.subMenu &&
                    (open ? (
                      <ExpandLess sx={{ color: 'text.secondary' }} />
                    ) : (
                      <ExpandMore sx={{ color: 'text.secondary' }} />
                    ))}
                </ListItemButton>
              </ListItem>

              {item.subMenu && item.subMenuItems && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    dense
                    disablePadding
                    onMouseEnter={
                      !isMobile
                        ? () => {
                            if (isVideos) setIsHoveringVideos(true);
                            if (isGuides) setIsHoveringGuides(true);
                          }
                        : undefined
                    }
                    onMouseLeave={
                      !isMobile
                        ? () => {
                            if (isVideos) setIsHoveringVideos(false);
                            if (isGuides) setIsHoveringGuides(false);
                          }
                        : undefined
                    }
                  >
                    {item.subMenuItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding sx={{ pl: 4 }}>
                        <ListItemButton
                          component={Link}
                          to={subItem.url}
                          selected={location.pathname === subItem.url}
                          onClick={closeDrawer}
                        >
                          <ListItemIcon>{subItem.icon}</ListItemIcon>
                          <ListItemText primary={subItem.text} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </Fragment>
          );
        })}
      </List>

      {/* Logout */}
      <List dense>
        <ListItem disablePadding onClick={handleLogout}>
          <ListItemButton>
            <ListItemIcon>
              <MeetingRoomRoundedIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Stack>
  );
}

